import type { User } from '../types';

// API設定を取得（api/client.tsと同じロジック）
const getApiBaseUrl = (): string => {
  // 環境変数が明示的に設定されている場合はそれを使用（ただしlocalhostは無視）
  if (import.meta.env.VITE_API_URL && !import.meta.env.VITE_API_URL.includes('localhost')) {
    return import.meta.env.VITE_API_URL;
  }
  
  // 本番環境（ポート443）の場合は同じドメインを使用
  if (window.location.port === '443' || window.location.protocol === 'https:') {
    return '';
  }
  
  // 開発環境の場合
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    // フロントエンドが5173で動作している場合、バックエンドは8080
    if (window.location.port === '5173') {
      return 'http://localhost:8080';
    }
    // それ以外の場合は同じドメインを使用
    return '';
  }
  
  // デフォルトは同じドメイン
  return '';
};

// ユーザー情報の管理（メモリ内で保持）
let currentUser: User | null = null;

// ユーザー情報を設定
export const setUserInMemory = (user: User | null): void => {
  currentUser = user;
};

// ユーザー情報を取得
export const getUserFromMemory = (): User | null => {
  return currentUser;
};

// サーバーから現在のユーザー情報を取得
export const fetchCurrentUser = async (): Promise<User | null> => {
  try {
    // fetch APIを直接使用
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // セッションCookieを送信
    });

    if (!response.ok) {
      console.log('User not authenticated, status:', response.status);
      currentUser = null;
      return null;
    }

    const user = await response.json();
    if (user && user.id) {
      currentUser = user;
      return user;
    }
    currentUser = null;
    return null;
  } catch (error) {
    console.error('Failed to get current user:', error);
    currentUser = null;
    return null;
  }
};

// 認証状態をチェック（サーバーに問い合わせ）
export const checkAuthStatus = async (): Promise<boolean> => {
  const user = await fetchCurrentUser();
  return !!user;
};

// ログアウト処理
export const logout = async (): Promise<void> => {
  try {
    const baseUrl = getApiBaseUrl();
    await fetch(`${baseUrl}/api/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // メモリとlocalStorageをクリア
    currentUser = null;
    localStorage.removeItem('user');
  }
};

// セッションベースの認証状態確認
export const isLoggedIn = (): boolean => {
  return !!currentUser || !!localStorage.getItem('user');
};